import { useTracking } from 'app/tracking/TrackingProvider';
import { useIsCodelistFeatureEnabled } from 'features/codelist/useIsCodeListEnabled';
import { Button } from 'features/components/Button';
import { Dialog } from 'features/entry-form-v2/component/Dialog';
import { useLocalStorage } from 'features/hooks/useLocalStorage';
import { useEffect, useState } from 'react';
import { EnableCodelistInExistingProjectsStep } from './EnableCodelistInExistingProjectStep';
import { EnableCodelistInNewProjectsStep } from './EnableCodelistInNewProjectStep';
import { IntroStep } from './IntroStep';
import { LanguageType } from 'types';
import { useLanguage } from 'features/hooks/useLanguage';

export const CODELIST_INTRO_DIALOG_KEY = 'has_seen_codelist_intro_dialog';

export const CodelistIntroDialog = () => {
	const [hasSeenCodelistIntro, setHasSeenCodelistIntro] = useLocalStorage(
		CODELIST_INTRO_DIALOG_KEY,
		false
	);
	const codelistEnabled = useIsCodelistFeatureEnabled();
	const language = useLanguage();
	const translation = DICTIONARY[language];
	const { track } = useTracking();

	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		if (hasSeenCodelistIntro || !codelistEnabled) return;

		setIsOpen(true);
	}, [hasSeenCodelistIntro, codelistEnabled]);

	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const currentStep = STEPS[currentStepIndex];

	const closeDialog = () => {
		setIsOpen(false);
		setHasSeenCodelistIntro(true);
	};

	return (
		<Dialog
			title={translation.title}
			open={isOpen}
			onClose={() => {
				track({
					eventName: 'codelist-intro-dialog-closed'
				});
				closeDialog();
			}}
		>
			<div className="flex flex-col gap-4 w-[600px] h-[600px]">
				{currentStep === 'intro' && <IntroStep />}
				{currentStep === 'enable-codelist-in-new-projects' && (
					<EnableCodelistInNewProjectsStep />
				)}
				{currentStep === 'enable-codelist-in-existing-projects' && (
					<EnableCodelistInExistingProjectsStep />
				)}
			</div>

			<div className="flex flex-row gap-4 items-end justify-between">
				<div className="text-sm">
					{translation.step_x_of_total(currentStepIndex + 1, STEPS.length)}
				</div>

				<div className="flex flex-row gap-4">
					{currentStepIndex > 0 && (
						<Button
							text={translation.previous}
							variant="secondary"
							onClick={() => setCurrentStepIndex(currentStepIndex - 1)}
						/>
					)}

					{currentStepIndex < STEPS.length - 1 && (
						<Button
							text={translation.next}
							onClick={() => {
								setCurrentStepIndex(currentStepIndex + 1);
								track({
									eventName: `codelist_intro-${currentStep}_completed`
								});
							}}
						/>
					)}

					{currentStepIndex === STEPS.length - 1 && (
						<Button
							text={translation.done}
							variant="primary"
							onClick={() => {
								closeDialog();
								track({
									eventName: 'codelist-intro-dialog-completed'
								});
							}}
						/>
					)}
				</div>
			</div>
		</Dialog>
	);
};

const STEPS = ['intro', 'enable-codelist-in-new-projects', 'enable-codelist-in-existing-projects'];

export const DICTIONARY = {
	[LanguageType.English]: {
		title: 'Personal identifiable information in Ledidi',
		step_x_of_total: (step: number, total: number) => `Step ${step} of ${total}`,
		done: 'Done',
		previous: 'Previous',
		next: 'Next'
	},
	[LanguageType.Norwegian]: {
		title: 'Personlig identifiserbar informasjon i Ledidi',
		step_x_of_total: (step: number, total: number) => `Steg ${step} av ${total}`,
		done: 'Ferdig',
		previous: 'Forrige',
		next: 'Neste'
	}
};
